// Angular
import { Observable, finalize, takeUntil, tap } from 'rxjs';
import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Libraries

import { TranslateService } from '@ngx-translate/core';

// Store
import { Store } from '@ngxs/store';
import { AuthActions } from '../../state/auth.action';
import { UserAuthActions } from '../../state/user-auth.action';

// Services
import { CoreService } from '../../../../shared/services';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Auth
import { AuthService } from '../..';
import { AppCleanup } from '../../../../shared/models';

// Helpers
import { FormHelper } from '../../../../shared/helpers';

// Enums
import { StatusType } from '../../../../shared/enums';

@Component({
	selector: 'login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})
export class LoginComponent extends AppCleanup implements OnInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	form: FormGroup;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];
	showPassword: boolean = false;
	validations = {
		userName: {
			min: 3,
			max: 320,
		},
		password: {
			min: 6,
			max: 100,
		},
	};

	private returnUrl: any;

	// Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	constructor(
		private router: Router,
		private auth: AuthService,
		private translateService: TranslateService,
		private formBuilder: FormBuilder,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute,
		private store: Store,
		private coreService: CoreService,
		private spinnerService: SpinnerService
	) {
		super();
	}

	ngOnInit() {
		this.initLoginForm();

		// redirect back to the returnUrl before login
		this.route.queryParams.pipe(takeUntil(this.destroy)).subscribe((params) => {
			this.returnUrl = params.returnUrl || '/';
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initLoginForm() {
		const NO_WHITESPACE = FormHelper.CUSTOM_VALIDATORS.NO_WHITESPACE;

		this.form = this.formBuilder.group({
			userName: [
				null,
				Validators.compose([
					Validators.required,
					NO_WHITESPACE,
					Validators.minLength(this.validations.userName.min),
					Validators.maxLength(this.validations.userName.max), // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
				]),
			],
			password: [null, Validators.compose([Validators.required, NO_WHITESPACE, Validators.minLength(this.validations.password.min), Validators.maxLength(this.validations.password.max)])],
		});
	}

	submit() {
		const controls = this.form.controls;
		/** check form */
		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		const authData = {
			userName: controls.userName.value,
			password: controls.password.value,
		};

		this.spinnerService.showAppSpinner(); // Turns on app wide spinner

		this.auth
			.login(authData.userName, authData.password)
			.pipe(
				tap((user) => {
					if (user) {
						this.store.dispatch(new AuthActions.SetToken(user.accessToken));
						this.store.dispatch(new UserAuthActions.SetUser(user));
						let jwtData = user.accessToken.split('.')[1];
						let decodedJwtJsonData = window.atob(jwtData);
						let decodedJwtData = JSON.parse(decodedJwtJsonData);
						let roles: string[] = decodedJwtData['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

						if (roles && roles.includes('PendingUser')) {
							this.router.navigateByUrl(`auth/change-password?token=${user.accessToken}&username=${user.username}`);
						} else {
							this.router.navigateByUrl(this.returnUrl); // Main page
						}
					} else {
						const message = this.translateService.instant('MODULE.AUTH.VALIDATION.INVALID_LOGIN');
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				}),
				takeUntil(this.destroy),
				finalize(() => {
					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					this.cdr.markForCheck();
				})
			)
			.subscribe();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}
}
